import { action, makeAutoObservable, toJS } from "mobx";
import { RootStore } from "../root";
import { getCoordinates } from "../../helpers/geolocation";
import { safe_get, buildLanguageMap } from "../../features/survey-builder/utils/survey-utils";
import { SurveyResponse, NavigationEntry } from "./survey-types";   
import { WebLink, CreateWebLinkRequest, UpdateWebLinkRequest, ListWebLinksRequest, ListWebLinksResponse } from "./weblink-types";
import { generateUniqueClientIdForSurveyResponse, getCurrentDateTime } from "./utils";

export class ActiveSurveyStore {
  rootStore: RootStore;
  activeSurvey: any = null;
  currentQuestionIndex: number = -1;
  responses: SurveyResponse[] = [];
  navigation: NavigationEntry[] = [];
  language: string = "en";
  lat: number = 0;
  long: number = 0;
  started: boolean = false;
  finished: boolean = false;
  previousSelectedOptionIndex: Record<string, number> = {};

  // Survey metadata
  projectid: number = 0;
  surveyid: number = 0;
  userid: number = 0;
  clientid: string = "";
  surveydate: string = "";

  // Decrypted interview link data
  decryptedInterviewData: any = null;

  // WebLink Management Properties
  weblinks: WebLink[] = [];
  allWeblinks: WebLink[] = []; // For client-side filtering
  weblinksLoading: boolean = false;
  weblinksTotal: number = 0;
  weblinksOffset: number = 0;
  weblinksLimit: number = 50;

  // Modal states
  createWebLinkModalOpen: boolean = false;
  editWebLinkModalOpen: boolean = false;
  deleteWebLinkModalOpen: boolean = false;
  activeWebLink: WebLink | null = null;

  // Bulk operation state
  selectedWebLinkIds: number[] = [];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  // Computed properties for survey progress (matching mobile behavior)
  get totalDisplayQuestions(): number {
    if (!this.activeSurvey?.questions) return 0;
    return this.activeSurvey.questions.filter(
      (q: any) => !q.skip_count
    ).length;
  }

  get currentDisplayQuestionIndex(): number {
    if (this.currentQuestionIndex === -1) return -1;
    
    const questions = this.activeSurvey?.questions || [];
    let displayIndex = 0;
    
    // Count how many displayable (non-skip_count) questions we've encountered up to current index
    for (let i = 0; i <= this.currentQuestionIndex && i < questions.length; i++) {
      if (!questions[i].skip_count) {
        displayIndex++;
      }
    }
    
    // Return 0-based index (so if we've seen 1 displayable question, return 0)
    return displayIndex - 1;
  }

  get progressPercentage(): number {
    if (this.currentQuestionIndex === -1) return 0;
    
    const total = this.totalDisplayQuestions;
    if (total === 0) return 0;
    
    // Match mobile behavior: progress = (currentQuestionIndex / totalQuestions) * 100
    // Progress is based on position through questions, not completion
    // When on first question (index 0), progress = 0%
    // When on last question (index total-1), progress = (total-1)/total * 100%
    const currentDisplayIndex = this.currentDisplayQuestionIndex;
    return Math.round((100.0 * currentDisplayIndex) / total);
  }

  get shouldHideProgress(): boolean {
    return this.activeSurvey?.meta?.hide_progress === true;
  }

  

  @action
  async beginSurvey(survey: any, decryptedData?: any) {
    this.activeSurvey = survey;
    this.currentQuestionIndex = -1;
    this.responses = [];
    this.navigation = [];
    this.started = true;
    this.finished = false;
    this.previousSelectedOptionIndex = {};

    // Store decrypted interview link data if provided
    if (decryptedData) {
      this.decryptedInterviewData = decryptedData;
    }

    // Get geolocation
    try {
      const coords = await getCoordinates();
      this.lat = coords.latitude;
      this.long = coords.longitude;
    } catch (error) {
      console.error("Geolocation error:", error);
      this.lat = 0;
      this.long = 0;
    }

    // Initialize survey metadata
    this.projectid = survey.projectid || 0;
    this.surveyid = survey.id || 0;
    this.userid = this.decryptedInterviewData?.surveyor?.id || 0;
    this.clientid = generateUniqueClientIdForSurveyResponse(
      this.projectid,
      this.surveyid,
      this.userid
    );
    this.surveydate = getCurrentDateTime();

    // Initialize empty responses array
    this.responses = survey?.questions?.map((question: any) => ({
      questionReference: JSON.parse(JSON.stringify(question)),
      response: {},
    }));

    // Add initial navigation entry
    this.pushNavigation("start", "intro", Date.now(), this.lat, this.long);

    // Get available languages from survey
    const introText = survey.meta?.intro_text;
    let availableLanguages: string[] = [];
    if (introText && typeof introText === "object") {
      availableLanguages = Object.keys(introText);
    } else {
      availableLanguages = survey.meta?.languages || ["en"];
    }

    // Build language map and get default language (first available)
    const languageMap = buildLanguageMap(availableLanguages);
    const languageMapKeys = Object.keys(languageMap);
    const defaultLanguage = languageMapKeys && languageMapKeys.length > 0 ? languageMapKeys[0] : "en";

    // Set language if current language is valid, otherwise use default
    const currentLanguage = this.language || "en";
    if (languageMapKeys.includes(currentLanguage)) {
      this.language = currentLanguage;
    } else {
      // Current language is not in available languages, set to default
      this.language = defaultLanguage;
    }
  }

  @action
  updateResponse(questionIndex: number, question: any, response: any) {
    if (this.responses[questionIndex]) {
      this.responses[questionIndex].response = JSON.parse(JSON.stringify(response));
    }
  }

  @action
  pushNavigation(from: string, to: string, ts: number, lat: number, long: number) {
    this.navigation.push({ from, to, ts, lat, long });
  }

  // Helper function to check if a question should be skipped based on entry_condition
  private shouldSkipQuestion(question: any): boolean {
    const entryCondition = safe_get(question, "payload.entry_condition", null);
    if (!entryCondition) return false;

    const isExists = safe_get(entryCondition, "exists", false);
    const qkey = safe_get(entryCondition, "qkey", null);
    const qvalue = safe_get(entryCondition, "qvalue", null);

    if (!isExists || !qkey || !qvalue) return false;

    // Find the referenced question
    const skipReferenceQuestion = this.activeSurvey.questions.find(
      (q: any) => q.name === qkey
    );
    if (!skipReferenceQuestion) return false;

    // Find the response for the referenced question
    const activeResponse = this.responses.find(
      (res) => res.questionReference.name === qkey
    );
    if (!activeResponse || !activeResponse.response) return false;

    const questionType = skipReferenceQuestion.type || "";
    const qvalueArray = qvalue[this.language] || qvalue.en || [];

    // Handle different question types
    if (questionType.includes("single-select")) {
      // For single-select questions: response.selected[language] is a string
      const selectedValue = safe_get(
        activeResponse.response,
        `selected.${this.language}`,
        null
      ) || safe_get(activeResponse.response, "selected.en", null);
      
      if (selectedValue && qvalueArray.includes(selectedValue)) {
        return true; // Should skip
      }
    } else if (questionType.includes("multi-select")) {
      // For multi-select questions: response.selected[language] is an array
      const selectedValues = safe_get(
        activeResponse.response,
        `selected.${this.language}`,
        []
      ) || safe_get(activeResponse.response, "selected.en", []);
      
      // Check if any selected value is in the qvalue array
      if (Array.isArray(selectedValues)) {
        const hasMatch = selectedValues.some((val: any) => 
          qvalueArray.includes(val)
        );
        if (hasMatch) {
          return true; // Should skip
        }
      }
    }

    return false;
  }

  @action
  goToNext() {
    if (this.currentQuestionIndex < this.activeSurvey.questions.length - 1) {
      let newIndex = this.currentQuestionIndex + 1;
      const questions = this.activeSurvey.questions;

      // Handle skip logic - check entry_condition for upcoming questions
      while (newIndex < questions.length) {
        const nextQuestion = questions[newIndex];
        if (!nextQuestion) break;

        if (this.shouldSkipQuestion(nextQuestion)) {
          newIndex += 1;
        } else {
          break;
        }
      }

      // Make sure we don't go beyond the last question
      if (newIndex >= questions.length) {
        newIndex = questions.length - 1;
      }

      this.pushNavigation(
        this.currentQuestionIndex.toString(),
        newIndex.toString(),
        Date.now(),
        this.lat,
        this.long
      );
      this.currentQuestionIndex = newIndex;
    }
  }

  @action
  goToPrev() {
    if (this.currentQuestionIndex > 0) {
      let newIndex = this.currentQuestionIndex - 1;
      const questions = this.activeSurvey.questions;

      // Handle skip logic - check entry_condition for previous questions
      while (newIndex >= 0) {
        const prevQuestion = questions[newIndex];
        if (!prevQuestion) break;

        if (this.shouldSkipQuestion(prevQuestion)) {
          newIndex -= 1;
        } else {
          break;
        }
      }

      // Make sure we don't go below 0
      if (newIndex < 0) {
        newIndex = 0;
      }

      this.pushNavigation(
        this.currentQuestionIndex.toString(),
        newIndex.toString(),
        Date.now(),
        this.lat,
        this.long
      );
      this.currentQuestionIndex = newIndex;
    }
  }

  @action
  updatePreviousSelectedOptionIndex(indexMap: Record<string, number>) {
    this.previousSelectedOptionIndex = { ...this.previousSelectedOptionIndex, ...indexMap };
  }

  @action
  setLanguage(language: string) {
    this.language = language;
  }

  @action
  finishSurvey() {
    this.finished = true;
  }

  @action
  assemblePayload(): any {
    const now = Date.now();
    const nowISO = getCurrentDateTime();
    
    // Convert surveydate ISO string to milliseconds if it's a string
    const startTimestamp = typeof this.surveydate === 'string' 
      ? new Date(this.surveydate).getTime() 
      : this.surveydate;
    
    const payload: any = {
      projectid: this.projectid,
      surveyid: this.surveyid,
      userid: this.userid,
      clientid: this.clientid,
      surveydate: this.surveydate, // Keep as ISO string
      uploaddate: nowISO, // Keep as ISO string
      trial: false,
      response: {
        response: this.responses.map((r) => r.response),
      },
      meta: {
        nav: this.navigation, // Already has numeric timestamps
        lat: this.lat,
        long: this.long,
        audiofile: "", // Empty string for web
        dates: {
          start: startTimestamp,
          update: now,
          end: now,
          uploaded: now,
          synced: false,
        },
        surveyref: this.activeSurvey,
        hierarchy: {}, // Empty object for web
      },
      qa: {},
      voterref: {}, // Empty object
      reflink: this.decryptedInterviewData?.metadata?.guid || null,
    };

    return payload;
  }

  @action
  reset() {
    this.activeSurvey = null;
    this.currentQuestionIndex = -1;
    this.responses = [];
    this.navigation = [];
    this.language = "en";
    this.lat = 0;
    this.long = 0;
    this.started = false;
    this.finished = false;
    this.previousSelectedOptionIndex = {};
    this.projectid = 0;
    this.surveyid = 0;
    this.userid = 0;
    this.clientid = "";
    this.surveydate = "";
    this.decryptedInterviewData = null;
  }

  // WebLink Management Methods

  @action
  async listWebLinks(projectid: number, offset: number = 0, count: number = 10): Promise<ListWebLinksResponse | null> {
    try {
      this.weblinksLoading = true;
      const payload: ListWebLinksRequest = {
        projectid,
        offset,
        count,
      };
      const response = await this.rootStore.api.post("survey-weblink/list", payload);
      
      if (response.ok && response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
        const data = response.data as ListWebLinksResponse;
        this.weblinks = data.weblinks;
        this.allWeblinks = data.weblinks; // Store all for client-side filtering
        this.weblinksTotal = data.total;
        this.weblinksOffset = data.offset;
        this.weblinksLimit = data.limit;
        return data;
      }
      return null;
    } catch (error) {
      console.error("Error listing weblinks:", error);
      return null;
    } finally {
      this.weblinksLoading = false;
    }
  }

  @action
  async createWebLink(payload: CreateWebLinkRequest): Promise<boolean> {
    try {
      const response = await this.rootStore.api.post("survey-weblink/create", payload);
      
      if (response.ok && response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
        // Refresh the list
        if (payload.projectid) {
          await this.listWebLinks(payload.projectid);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error creating weblink:", error);
      return false;
    }
  }

  @action
  async updateWebLink(id: number, updates: Partial<UpdateWebLinkRequest>): Promise<boolean> {
    try {
      const payload: UpdateWebLinkRequest = {
        id,
        ...updates,
      };
      const response = await this.rootStore.api.post("survey-weblink/update", payload);
      
      if (response.ok && response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
        // Refresh the list
        const weblink = this.weblinks.find((w) => w.id === id);
        if (weblink?.projectid) {
          await this.listWebLinks(weblink.projectid);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating weblink:", error);
      return false;
    }
  }

  @action
  async deleteWebLink(id: number): Promise<boolean> {
    try {
      const response = await this.rootStore.api.post("survey-weblink/delete", { id });
      
      if (response.ok && response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
        // Refresh the list
        const weblink = this.weblinks.find((w) => w.id === id);
        if (weblink?.projectid) {
          await this.listWebLinks(weblink.projectid);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error deleting weblink:", error);
      return false;
    }
  }

  @action
  async toggleWebLinkActive(id: number, active: boolean): Promise<boolean> {
    try {
      const response = await this.rootStore.api.post("survey-weblink/toggle-active", {
        id,
        active,
      });
      
      if (response.ok && response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
        // Refresh the list
        const weblink = this.weblinks.find((w) => w.id === id);
        if (weblink?.projectid) {
          await this.listWebLinks(weblink.projectid);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error toggling weblink active status:", error);
      return false;
    }
  }

  @action
  async bulkToggleWebLinkActive(ids: number[], active: boolean): Promise<boolean> {
    try {
      const promises = ids.map((id) => this.toggleWebLinkActive(id, active));
      const results = await Promise.all(promises);
      return results.every((result) => result === true);
    } catch (error) {
      console.error("Error bulk toggling weblink active status:", error);
      return false;
    }
  }

  @action
  async bulkDeleteWebLinks(ids: number[]): Promise<boolean> {
    try {
      const promises = ids.map((id) => this.deleteWebLink(id));
      const results = await Promise.all(promises);
      return results.every((result) => result === true);
    } catch (error) {
      console.error("Error bulk deleting weblinks:", error);
      return false;
    }
  }

  @action
  setCreateWebLinkModalOpen(status: boolean) {
    this.createWebLinkModalOpen = status;
  }

  @action
  setEditWebLinkModalOpen(status: boolean) {
    this.editWebLinkModalOpen = status;
  }

  @action
  setDeleteWebLinkModalOpen(status: boolean) {
    this.deleteWebLinkModalOpen = status;
  }

  @action
  setActiveWebLink(weblink: WebLink | null) {
    this.activeWebLink = weblink;
  }

  @action
  setSelectedWebLinkIds(ids: number[]) {
    this.selectedWebLinkIds = ids;
  }

  generateWebLinkPublicUrl(guid: string): string {
    return `https://app.forthepeople.ai/interview/web/${guid}`;
  }
}

